import { Dispatch, FC, useEffect, useRef, useState } from "react"

import { ReactComponent as Salary } from "@common_assets/svg/greenDollar.svg"
import { ReactComponent as Infinite } from "@common_assets/svg/infinity.svg"
import { ReactComponent as Suitcase } from "@common_assets/svg/suitcase.svg"
import { ReactComponent as UserTeacher } from "@common_assets/svg/userTeacher.svg"
import { Stack, Box, Typography, Button, Divider } from "@mui/material"
import { useMutationStudentJobApplications } from "api/reactQuery/mutations/students"
import { IGetStudentPossibleJobsInClass } from "api/reactQuery/queries/studentJobs.types"
import Dialog from "components/common/dialog/dialog"
import Coin from "components/common/icon/coin"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useDialog } from "hooks/dialog"
import { useCustomSnackbar } from "hooks/snackbar"
import RichEditor from "pages/jobs/RichEditor"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { getErrorMessageFromTab } from "utils/api"

import { JOBS_PANEL_VIEW_BREAKPOINT } from "../JobsPanel.config"
import NoSpecifyJob from "../noSpecifyJob"
import ApplicationModule from "./applicationModule"

const JobsOfferSpecify: FC<{
  selectedJob: IGetStudentPossibleJobsInClass | null
  isExpanded: boolean
  setSelectedJob: Dispatch<React.SetStateAction<IGetStudentPossibleJobsInClass | null>>
}> = ({ selectedJob, isExpanded, setSelectedJob }) => {
  const editorsWrapperRef = useRef<HTMLDivElement | null>(null)
  const [height, setHeight] = useState(0)
  const isRowView = useIsBreakpointUp(JOBS_PANEL_VIEW_BREAKPOINT)
  const isTablet = useIsBreakpointUp("tablet")
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()
  const { showSnackbar } = useCustomSnackbar()

  const {
    handleOpen: handleOpenApplicationDialog,
    isOpen: isOpenApplicationDialog,
    handleClose: handleCloseApplicationDialog,
  } = useDialog()

  const { mutate: postJobApplication, isLoading: isLoadingPostJobApplication } =
    useMutationStudentJobApplications({
      options: {
        onSuccess: () => {
          queryClient.invalidateQueries("studentJobsInClass")
          if (selectedJob) {
            setSelectedJob({
              ...selectedJob,
              pending_application: true
            })
          }
          showSnackbar({
            title: t("studentJobs.yourApplicationHasBeenSentSuccessfully"),
          })
          handleCloseApplicationDialog()
        },
        onError: (error) => {
          showSnackbar({
            title: t(getErrorMessageFromTab(error)),
            variant: "error",
          })
        },
      },
    })

  useEffect(() => {
    if (
      editorsWrapperRef.current &&
      editorsWrapperRef.current.clientHeight !== height
    )
      setHeight(editorsWrapperRef.current.clientHeight)
  })

  return (
    <>
      {!selectedJob ? (
        <NoSpecifyJob />
      ) : (
        <Stack
          sx={(theme) => ({
            borderRadius: "8px",
            backgroundColor: "mockup.primary99",
            width: "100%",
            px: 0,
            py: 0,
            overflowY: "auto",
            maxHeight: {
              mobile: "none",
              desktop: "calc(100vh - 68px - 82px)",
            },
            [theme.breakpoints.up("tablet")]: {
              px: 3,
              py: 4,
              backgroundColor: "mockup.neutral100",
            },
          })}
          className="smooth-scrollbar"
        >
          <Box
            sx={{
              px: { mobile: 0, tablet: 3 },
              mb: { mobile: 0, tablet: 3 },
              display: "flex",
              flexDirection: { mobile: "column", tablet: "row" },
              justifyContent: "space-between",
              alignItems: { mobile: "start", tablet: "center" },
           
            }}
          >
            <Typography fontSize="24px" color="mockup.neutral10">
              {selectedJob.title}
            </Typography>
            {isTablet && (
              <Box
                display="flex"
                alignItems="center"
                width="auto"
              >
                {selectedJob.pending_application ? (
                  <Box
                    sx={{
                      backgroundColor: "mockup.secondary90",
                      color: "mockup.secondary60",
                      p: "4px 8px",
                      borderRadius: "6px",
                    }}
                  >
                    {t("studentJobs.pending").toUpperCase()}
                  </Box>
                ) : selectedJob.is_hired ? (
                  <Box
                    sx={{
                      backgroundColor: "mockup.success90",
                      color: "mockup.success60",
                      p: "4px 8px",
                      borderRadius: "6px",
                    }}
                  >
                    {t("studentJobs.hired").toUpperCase()}
                  </Box>
                ) : (
                  <Button
                    disabled={
                      Number(selectedJob.positions) - selectedJob.employees_count ===
                        0 && selectedJob.positions !== null
                    }
                    sx={{ height: "100%" }}
                    onClick={handleOpenApplicationDialog}
                  >
                    {t("studentJobs.applyNow")}
                  </Button>
                )}
              </Box>
            )}
            {!isTablet && (selectedJob.pending_application || selectedJob.is_hired) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  width: "100%",
                  mt: 1,
                }}
              >
                {selectedJob.pending_application ? (
                  <Box
                    sx={{
                      backgroundColor: "mockup.secondary90",
                      color: "mockup.secondary60",
                      p: "4px 8px",
                      borderRadius: "6px",
                    }}
                  >
                    {t("studentJobs.pending").toUpperCase()}
                  </Box>
                ) : selectedJob.is_hired ? (
                  <Box
                    sx={{
                      backgroundColor: "mockup.success90",
                      color: "mockup.success60",
                      p: "4px 8px",
                      borderRadius: "6px",
                    }}
                  >
                    {t("studentJobs.hired").toUpperCase()}
                  </Box>
                ) : null}
              </Box>
            )}
          </Box>
          {!isTablet && (
            <Divider
              sx={{
                width: "100%",
                my: 3,
              }}
            />
          )}
          <Dialog
            open={isOpenApplicationDialog}
            onClose={handleCloseApplicationDialog}
            titleText={t("studentJobs.fillOutTheApplication")}
            isLoading={isLoading || isLoadingPostJobApplication}
            actionAcceptButtonProps={{
              disabled: isLoading || isLoadingPostJobApplication,
              type: "submit",
              form: "form",
            }}
          >
            <ApplicationModule
              setIsLoading={setIsLoading}
              postJobApplication={postJobApplication}
              selectedJob={selectedJob}
              id="form"
            />
          </Dialog>
          {isTablet && <Divider style={{ width: "100%" }} />}
          <Box
            sx={{
              px: { mobile: 0, tablet: 3 },
              minHeight: "46px",
              my: { mobile: 0, tablet: 3 },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box
              px={{ mobile: 0, tablet: 3 }}
              width="100%"
              height="100%"
              display="flex"
              flexDirection={{ mobile: "column", tablet: "row" }}
              justifyContent="space-between"
              gap="16px"
            >
              <Box display="flex">
                <UserTeacher />
                <Box ml={2}>
                  <Typography variant="subtitle1" color="mockup.neutral70">
                    {t("studentJobs.employer")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="mockup.neutral10"
                    fontWeight="500"
                  >
                    {selectedJob.teacher?.first_name
                      ? selectedJob.teacher.first_name
                      : "-"}{" "}
                    {selectedJob.teacher?.last_name
                      ? selectedJob.teacher.last_name
                      : ""}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex">
                <Suitcase />
                <Box ml={2}>
                  <Typography variant="subtitle1" color="mockup.neutral70">
                    {t("studentJobs.openPositions")}:
                  </Typography>
                  <Typography
                    variant="body2"
                    color="mockup.neutral10"
                    fontWeight="500"
                  >
                    {selectedJob.positions === null ? (
                      <Infinite />
                    ) : (
                      Number(selectedJob.positions) - selectedJob.employees_count
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box display="flex">
                <Salary />
                <Box ml={2}>
                  <Typography variant="subtitle1" color="mockup.neutral70">
                    {t("studentJobs.salary")}:
                  </Typography>
                  <Coin amount={selectedJob.salary} />
                </Box>
              </Box>
            </Box>
          </Box>
          {!isTablet && (
            <Divider
              sx={{
                width: "100%",
                mt: 3,
              }}
            />
          )}
          <Stack
            sx={{
              maxHeight: isRowView ? "none" : isExpanded ? height : 0,
              transition: "max-height 0.5s",
              overflowY: "auto",
            }}
            className="smooth-scrollbar"
          >
            <Stack ref={editorsWrapperRef}>
              {isTablet && <Divider style={{ width: "100%" }} />}
              <Box
                sx={{
                  px: { mobile: 0, tablet: 3 },
                  mt: 3,
                }}
              >
                <Box mb={3}>
                  <Typography
                    variant="subtitle1"
                    color="mockup.neutral70"
                    mb={2}
                  >
                    {t("studentJobs.responsibilities")}:
                  </Typography>
                  <RichEditor
                    state={selectedJob.responsibilities}
                    id={selectedJob.id}
                    readOnly={true}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    color="mockup.neutral70"
                    mb={2}
                  >
                    {t("studentJobs.qualifications")}:
                  </Typography>
                  <RichEditor
                    state={selectedJob.qualifications}
                    readOnly={true}
                    id={selectedJob.id}
                  />
                </Box>
              </Box>
            </Stack>
          </Stack>
          {!isTablet && !selectedJob.pending_application && !selectedJob.is_hired && (
            <Box
              sx={{
                px: { mobile: 0, tablet: 3 },
                pt: 3,
                pb: { mobile: 3, tablet: 0 },
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <Button
                disabled={
                  Number(selectedJob.positions) - selectedJob.employees_count ===
                    0 && selectedJob.positions !== null
                }
                onClick={handleOpenApplicationDialog}
              >
                {t("studentJobs.applyNow")}
              </Button>
            </Box>
          )}
        </Stack>
      )}
    </>
  )
}

export default JobsOfferSpecify
